/**
 * Builds the redirect a non-public landing-page gate sends an unauthenticated
 * visitor to. SSO-gated pages (`sso_domain` set) go straight to enforced SSO via
 * `/auth/sso-start` so the visitor never sees the email/Google picker; everyone
 * else hits the normal login (the `event` param drives the "sign in with your
 * invited email" banner, and `access=require_login` swaps that banner's copy for
 * pages/forms that don't actually require a specific invited email). Centralizes
 * the `/auth/sso-start` query contract so it stays in sync across the page,
 * subpage, and attendee-dashboard gates.
 *
 * @param ssoDomain - The page's enforced SSO domain (event_landing_pages.sso_domain), or null when not gated.
 * @param nextPath - Internal path to return to after auth (e.g. `/${slug}`). Must be an app-relative path.
 * @param eventId - Event id, used for the login invited-email banner.
 * @param requireLogin - True when the gate is `require_login` (any authenticated user, no invite
 *   roster) rather than a true invite-only gate — changes the login page's banner copy.
 * @returns A relative URL string suitable for `redirect()`.
 */
export function buildLandingPageAuthRedirect(
  ssoDomain: string | null,
  nextPath: string,
  eventId: number,
  requireLogin = false
): string {
  if (ssoDomain) {
    return `/auth/sso-start?domain=${encodeURIComponent(ssoDomain)}&next=${encodeURIComponent(nextPath)}`;
  }
  const accessParam = requireLogin ? "&access=require_login" : "";
  return `/login?redirect=${encodeURIComponent(nextPath)}&event=${eventId}${accessParam}`;
}
